import { Campaign } from "@/types/campaign";
import { ListingOverview } from "@/types/listing";
import { auctionsLabels } from "@/i18n/labels/auctionsLabels";
import { getLanguage, type Language } from "@/utils/languageUtils";

/**
 * Effective auction end for countdowns — prefer anti-sniping extension over scheduled end.
 */
export const getListingAuctionEndTime = (
  listing: Pick<ListingOverview, "endTime" | "actualEndTime">,
): string | undefined => {
  return listing.actualEndTime || listing.endTime || undefined;
};

/** Poll list while any auction on the page is in the anti-sniping window. */
export const LISTINGS_ENDING_SOON_MS = 5 * 60 * 1000;
export const LISTINGS_ENDING_SOON_POLL_MS = 5_000;

/**
 * React Query refetchInterval: poll every 5s only when an auction ends within 5 minutes.
 * Returns `false` otherwise (no background spam).
 */
export function listingsEndingSoonRefetchInterval(
  listings:
    | Array<
        Pick<ListingOverview, "endTime" | "actualEndTime"> & {
          listingType?: string;
        }
      >
    | undefined,
): number | false {
  if (!listings?.length) return false;
  const now = Date.now();
  const endingSoon = listings.some((listing) => {
    if (listing.listingType && listing.listingType !== "auction") return false;
    const endRaw = getListingAuctionEndTime(listing);
    if (!endRaw) return false;
    const end = new Date(endRaw).getTime();
    if (Number.isNaN(end)) return false;
    const remaining = end - now;
    return remaining > 0 && remaining <= LISTINGS_ENDING_SOON_MS;
  });
  return endingSoon ? LISTINGS_ENDING_SOON_POLL_MS : false;
}

/**
 * Format mileage values (in mil) with space as thousands separator
 * Example: 100000 -> "100 000"
 */
export const formatMileage = (mileage: number | string | undefined | null): string => {
  if (mileage === undefined || mileage === null || mileage === "") return "0";
  const numValue = typeof mileage === "string" ? parseFloat(mileage) : mileage;
  if (isNaN(numValue)) return "0";
  return numValue.toLocaleString("sv-SE").replace(/,/g, " ");
};

// Campaign type utilities
export const getTypeColor = (type: string) => {
  switch (type) {
    case 'auction':
      return 'bg-orange-900/80 text-orange-300 border-orange-700';
    case 'direct-purchase':
      return 'bg-green-900/80 text-green-300 border-green-700';
      case 'direct':
      return 'bg-green-900/80 text-green-300 border-green-700';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export const getTypeLabel = (type: string, lang?: Language): string => {
  const listing = auctionsLabels(lang ?? getLanguage()).listing as { type?: Record<string, string> } | undefined;
  if (listing?.type && type in listing.type) return listing.type[type];
  switch (type) {
    case 'auction':
      return 'Auktion';
    case 'direct-purchase':
      return 'Direktköp';
    case 'direct':
      return 'Direktköp';
    default:
      return type;
  }
};

// Campaign status utilities
export const getStatusColor = (status: string) => {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'upcoming':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'completed':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    case 'cancelled':
      return 'bg-red-100 text-red-800 border-red-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export const getStatusLabel = (status: string, lang?: Language): string => {
  const listing = auctionsLabels(lang ?? getLanguage()).listing as { status?: Record<string, string> } | undefined;
  if (listing?.status && status in listing.status) return listing.status[status];
  switch (status) {
    case 'active': return 'Aktiv';
    case 'upcoming': return 'Kommande';
    case 'completed': return 'Avslutad';
    case 'cancelled': return 'Avbruten';
    default: return status;
  }
};

// Border color utilities
export const getBorderColor = (type: string) => {
  switch (type) {
    case 'auction':
      return 'border-b-orange-300 dark:border-b-orange-600';
    case 'direct-purchase':
      return 'border-b-green-300 dark:border-b-green-600';
      case 'direct':
      return 'border-b-green-300 dark:border-b-green-600';
    default:
      return 'border-b-gray-300 dark:border-b-gray-600';
  }
};

// Vehicle status utilities
export const getVehicleStatusColor = (status: string) => {
  switch (status) {
    case 'reserved':
      return 'text-orange-600';
    case 'active':
      return 'text-green-600';
    case 'completed':
      return 'text-blue-600';
    case 'ended':
      return 'text-gray-600';
    case 'cancelled':
      return 'text-red-600';
    case 'without-campaign':
      return 'text-orange-600';
    default:
      return 'text-gray-600';
  }
};

export const getVehicleStatusLabel = (status: string, lang?: Language): string => {
  const listing = auctionsLabels(lang ?? getLanguage()).listing as { vehicleStatus?: Record<string, string> } | undefined;
  if (listing?.vehicleStatus && status in listing.vehicleStatus) return listing.vehicleStatus[status];
  switch (status) {
    case 'active':
      return 'Aktiv';
    case 'reserved':
      return 'Reserverad';
    case 'completed':
      return 'Såld';
    case 'ended':
      return 'Avslutad';
    case 'cancelled':
      return 'Avbruten';
    case 'without-campaign':
      return 'Utan kampanj';
    default:
      return status;
  }
};

// Helper function to get campaign status (same as in CreateListingDialog)
export const getCampaignStatus = (campaign: Campaign) => {
  // If campaign has explicit cancelled or completed status, use that
  if (campaign.status === 'cancelled') {
    return 'cancelled';
  }
  if (campaign.status === 'completed') {
    return 'completed';
  }
  
  // Otherwise calculate based on dates
  const now = new Date();
  const startDate = new Date(campaign.startDate);
  const endDate = new Date(campaign.endDate);
  
  if (now >= startDate && now <= endDate) {
    return 'active';
  } else if (now < startDate) {
    return 'upcoming';
  } else {
    return 'completed';
  }
};

export const getTransportStatusLabel = (status: string, lang?: Language): string => {
  const listing = auctionsLabels(lang ?? getLanguage()).listing as { transportStatus?: Record<string, string> } | undefined;
  if (listing?.transportStatus && status in listing.transportStatus) return listing.transportStatus[status];
  switch (status) {
    case "not_started": return "Ej påbörjad";
    case "planned": return "Planerad";
    case "in_progress": return "Under transport";
    case "completed": return "Slutförd";
    default: return status;
  }
};

// Filter and sort utilities
export interface FilterState {
  make: string;
  model: string;
  minMileage: string;
  maxMileage: string;
  minYear: string;
  maxYear: string;
  transmission: string;
  fuelType: string;
  minPower: string;
  maxPower: string;
  bodyType: string;
  minPrice: string;
  maxPrice: string;
}

export const filterAndSortListings = (
  listings: ListingOverview[],
  searchTerm: string,
  sortBy: string,
  filters: FilterState
): ListingOverview[] => {
  return listings
    .filter((listing) => {
      const vehicle = listing.vehicleDetails;

      // Search term filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        searchTerm === "" ||
        vehicle.make?.toLowerCase().includes(searchLower) ||
        vehicle.model?.toLowerCase().includes(searchLower) ||
        (vehicle.year && vehicle.year.toString().includes(searchLower)) ||
        vehicle.regNumber?.toLowerCase().includes(searchLower);

      if (!matchesSearch) return false;

      // Make filter
      if (filters.make && vehicle.make !== filters.make) return false;

      // Model filter
      if (
        filters.model &&
        !vehicle.model.toLowerCase().includes(filters.model.toLowerCase())
      )
        return false;

      // Year range filter
      if (filters.minYear && vehicle.year < parseInt(filters.minYear))
        return false;
      if (filters.maxYear && vehicle.year > parseInt(filters.maxYear))
        return false;

      // Mileage range filter
      if (
        filters.minMileage &&
        vehicle.mileage < parseInt(filters.minMileage)
      )
        return false;
      if (
        filters.maxMileage &&
        vehicle.mileage > parseInt(filters.maxMileage)
      )
        return false;

      // Price range filter
      const currentPrice =
        listing.listingType === "auction"
          ? listing.auctionDetails?.currentBid ||
            listing.auctionDetails?.startingPrice ||
            0
          : listing.directPurchaseDetails?.currentPrice || 0;

      if (filters.minPrice && currentPrice < parseInt(filters.minPrice))
        return false;
      if (filters.maxPrice && currentPrice > parseInt(filters.maxPrice))
        return false;

      // Transmission filter
      if (
        filters.transmission &&
        vehicle.transmission !== filters.transmission
      )
        return false;

      // Fuel type filter
      if (filters.fuelType && vehicle.fuelType !== filters.fuelType)
        return false;

      // Body type filter
      if (filters.bodyType && vehicle.bodyType !== filters.bodyType)
        return false;

      return true;
    })
    .sort((a, b) => {
      const priceA =
        a.listingType === "auction"
          ? a.auctionDetails?.currentBid ||
            a.auctionDetails?.startingPrice ||
            0
          : a.directPurchaseDetails?.currentPrice || 0;

      const priceB =
        b.listingType === "auction"
          ? b.auctionDetails?.currentBid ||
            b.auctionDetails?.startingPrice ||
            0
          : b.directPurchaseDetails?.currentPrice || 0;

      switch (sortBy) {
        case "createdAt-desc": {
          const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return timeB - timeA;
        }
        case "price-asc":
          return priceA - priceB;
        case "price-desc":
          return priceB - priceA;
        case "year-desc":
          return b.vehicleDetails.year - a.vehicleDetails.year;
        case "year-asc":
          return a.vehicleDetails.year - b.vehicleDetails.year;
        case "mileage-asc":
          return a.vehicleDetails.mileage - b.vehicleDetails.mileage;
        case "mileage-desc":
          return b.vehicleDetails.mileage - a.vehicleDetails.mileage;
        case "make-asc":
          return (a.vehicleDetails?.make || "").localeCompare(
            b.vehicleDetails?.make || "",
            "sv",
            { sensitivity: "base" },
          );
        case "make-desc":
          return (b.vehicleDetails?.make || "").localeCompare(
            a.vehicleDetails?.make || "",
            "sv",
            { sensitivity: "base" },
          );
        case "endDate-desc": {
          const timeA = a.endTime ? new Date(a.endTime).getTime() : 0;
          const timeB = b.endTime ? new Date(b.endTime).getTime() : 0;
          return timeB - timeA;
        }
        case "endDate-asc": {
          const timeA = a.endTime ? new Date(a.endTime).getTime() : 0;
          const timeB = b.endTime ? new Date(b.endTime).getTime() : 0;
          return timeA - timeB;
        }
        default:
          return 0;
      }
    });
};

export function mapListingSortToApi(sortBy: string): string | undefined {
  switch (sortBy) {
    case "createdAt-desc":
      return "-createdAt";
    case "endDate-desc":
      return "-endTime";
    case "endDate-asc":
      return "endTime";
    case "make-asc":
      return "title";
    case "make-desc":
      return "-title";
    default:
      return undefined;
  }
}

/** Prefer company name, then username, then first+last, then email. */
export function resolveSellerDisplayName(seller?: {
  company?: { name?: string | null } | null;
  username?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
} | null): string | undefined {
  if (!seller) return undefined;
  const company = seller.company?.name?.trim();
  if (company) return company;
  const username = seller.username?.trim();
  if (username) return username;
  const full = [seller.firstName, seller.lastName]
    .map((p) => (typeof p === "string" ? p.trim() : ""))
    .filter(Boolean)
    .join(" ");
  if (full) return full;
  const email = seller.email?.trim();
  if (email) return email;
  return undefined;
}

export function resolveSellerSubtitle(seller?: {
  company?: { name?: string | null } | null;
  username?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  role?: string | null;
} | null): string | undefined {
  if (!seller) return undefined;
  const company = seller.company?.name?.trim();
  if (company) {
    const person =
      seller.username?.trim() ||
      [seller.firstName, seller.lastName]
        .map((p) => (typeof p === "string" ? p.trim() : ""))
        .filter(Boolean)
        .join(" ");
    return person || undefined;
  }
  if (seller.role === "dealer") return "Handlare";
  if (seller.role === "business") return "Företag";
  if (seller.role === "individual") return "Privatperson";
  return undefined;
}
